import { z } from 'zod';

/**
 * `pending-demo-project` — shapes for the demo-project seeder API.
 *
 *   POST /api/demo/ensure
 *     -> { seeded: boolean, projectId: string, flowIds: string[], skippedReason?: string }
 *
 * The seeder is idempotent. On the first call it stamps a row on
 * `projects` with `is_demo = true` plus three template flows and a
 * fake passing run. Subsequent calls return `seeded: false` + the
 * existing demo project id.
 */

export const EnsureDemoProjectResponseSchema = z.object({
  seeded: z.boolean(),
  projectId: z.string(),
  flowIds: z.array(z.string()),
  /** Returned only when `seeded === false`. */
  skippedReason: z.string().optional(),
  /** Hash-route the dashboard should jump to after seeding (e.g. `#/project/<id>`). */
  navigateTo: z.string().optional(),
});
export type EnsureDemoProjectResponse = z.infer<typeof EnsureDemoProjectResponseSchema>;

/**
 * The optional sample-app launcher. We bundle two tiny vanilla-HTML
 * apps that ship with the runner (`packages/mvp/demo-sites/`):
 *
 *   - `todo-app`  — minimal to-do list with localStorage persistence.
 *   - `login-app` — fake login form with deterministic credentials.
 *
 * Each is a static file served by the runner's webdriver host on
 * `http://localhost:<port>/__demo/<slug>/`.
 */
export const SampleAppIdSchema = z.enum(['todo-app', 'login-app', 'search-app']);
export type SampleAppId = z.infer<typeof SampleAppIdSchema>;

export const SampleAppSchema = z.object({
  id: SampleAppIdSchema,
  displayName: z.string(),
  description: z.string(),
  url: z.string(),
  /** Pre-canned flow id (when the demo project is seeded) that exercises this app. */
  exampleFlowName: z.string(),
});
export type SampleApp = z.infer<typeof SampleAppSchema>;

export const ListSampleAppsResponseSchema = z.object({
  apps: z.array(SampleAppSchema),
  /** True when the operator turned the launcher off in admin-config. */
  disabled: z.boolean().optional(),
});
export type ListSampleAppsResponse = z.infer<typeof ListSampleAppsResponseSchema>;

/**
 * Curated catalogue of sample apps. The runner / frontend both import
 * this constant so the URL shape stays in lockstep.
 */
export const SAMPLE_APPS: SampleApp[] = [
  {
    id: 'todo-app',
    displayName: 'To-do app',
    description: 'A tiny vanilla-HTML to-do list. Great for click + assert.',
    url: '/__demo/todo-app/',
    exampleFlowName: 'Add a todo and mark it done',
  },
  {
    id: 'login-app',
    displayName: 'Login form',
    description: 'Username + password form with deterministic credentials.',
    url: '/__demo/login-app/',
    exampleFlowName: 'Log in with demo credentials',
  },
  {
    id: 'search-app',
    displayName: 'Search demo',
    description: 'Type-to-filter list of fruits. Stress-tests text input + assertions.',
    url: '/__demo/search-app/',
    exampleFlowName: 'Search for "apple"',
  },
];
